import type { ProblemSetPdfLang, ProblemSetPdfPaper } from "../types";

/**
 * The problem set on paper, the client's half (`EX-PDF02`, `docs/29` §6.13,
 * `docs/07` "The problem set on paper").
 *
 * The one-press PDF sends no option at all: the server's defaults are the
 * founder's one press (every problem, A4, a cover with the setters and the
 * balloons, the round's language), so the console never restates them and
 * cannot drift from them. The options dialog sends every choice it shows.
 */

/** What `GET .../problem-set/pdf` takes. Every field is optional on the wire. */
export type ProblemSetPdfOptions = {
  letters?: readonly string[];
  lang?: ProblemSetPdfLang;
  paper?: ProblemSetPdfPaper;
  cover?: boolean;
  setters?: boolean;
  balloons?: boolean;
  newSheet?: boolean;
};

/** What `GET .../problems/:letter/pdf` takes: one problem, never a cover. */
export type ProblemPdfOptions = Pick<ProblemSetPdfOptions, "lang" | "paper" | "balloons">;

/** The options as a query string, `""` when there are none. Booleans are spelt `true`/`false`. */
export function problemSetPdfQuery(options: ProblemSetPdfOptions): string {
  const query = new URLSearchParams();
  if (options.letters && options.letters.length > 0) query.set("letters", options.letters.join(","));
  if (options.lang) query.set("lang", options.lang);
  if (options.paper) query.set("paper", options.paper);
  for (const key of ["cover", "setters", "balloons", "newSheet"] as const) {
    const value = options[key];
    if (value !== undefined) query.set(key, value ? "true" : "false");
  }
  const encoded = query.toString();
  return encoded ? `?${encoded.replace(/%2C/g, ",")}` : "";
}

/** The options dialog's state, one field per control it draws. */
export type ProblemSetPdfChoices = {
  /** The letters ticked, in any order; printed in contest order. */
  picked: ReadonlySet<string>;
  lang: ProblemSetPdfLang;
  paper: ProblemSetPdfPaper;
  cover: boolean;
  setters: boolean;
  newSheet: boolean;
  balloons: boolean;
};

/**
 * The request the dialog makes. Every choice is sent, so what the organizer
 * saw ticked is what prints; `letters` only when fewer than all are picked
 * (all of them is the server's default), and `setters` only with a cover,
 * where the setters' line lives.
 */
export function pdfOptionsOf(choices: ProblemSetPdfChoices, allLetters: readonly string[]): ProblemSetPdfOptions {
  const letters = allLetters.filter((letter) => choices.picked.has(letter));
  return {
    ...(letters.length < allLetters.length ? { letters } : {}),
    lang: choices.lang,
    paper: choices.paper,
    cover: choices.cover,
    ...(choices.cover ? { setters: choices.setters } : {}),
    newSheet: choices.newSheet,
    balloons: choices.balloons,
  };
}

/** The language the server picks when none is asked for: Bangla when every problem has a Bangla statement. */
export function defaultPdfLang(items: readonly { hasBanglaStatement?: boolean }[]): ProblemSetPdfLang {
  return items.length > 0 && items.every((item) => item.hasBanglaStatement === true) ? "bn" : "en";
}

/** The file name the server would give, for the rare answer that lost its header. */
export function fallbackPdfName(slug: string, letter?: string): string {
  return letter ? `${slug}-${letter.toLowerCase()}.pdf` : `${slug}-problems.pdf`;
}
